import React, { type FC } from 'react';
import { Link, Radio } from '@g2u/ui-kit';
import { Form, Spin, Collapse } from 'antd';
import { observer } from 'mobx-react-lite';

import type { RescheduleReason, WithOptionalDataTest } from 'types';
import { Section } from 'components';
import styles from '../reschedule.module.scss';

export namespace SelectRescheduleReason {
  export type Props = {
    disabled: boolean;
    selectedReason: number | null;
    isReasonsLoading: boolean;
    onSelect: (reasonId: number) => void;
    reasons?: RescheduleReason[];
  } & WithOptionalDataTest;

  type FormFields = { reason: string };

  const reasonGroupTitle = {
    C: 'Customer has initiated the reschedule',
    T: 'Technician has initiated the reschedule',
    O: 'Others',
    R: 'Others',
  } as const satisfies Record<RescheduleReason['category'], string>;

  export const $: FC<Props> = observer((props) => {
    const { isReasonsLoading, onSelect, reasons, disabled, selectedReason } = props;
    const [form] = Form.useForm<FormFields>();

    const handleFormChange = (changedField: Partial<FormFields>) => {
      changedField.reason && onSelect(parseInt(changedField.reason));
    };

    const groupedReasons = reasons
      ? Object.groupBy(reasons, ({ category }) => reasonGroupTitle[category] || 'Others')
      : {};

    return (
      <Section.$ title="Select a Reason" noPadding={!isReasonsLoading}>
        {isReasonsLoading ? (
          <Spin />
        ) : (
          <Form
            form={form}
            onValuesChange={handleFormChange}
            validateTrigger={'onBlur'}
            className={styles.reschedule__reason}
            disabled={disabled}
            preserve={true}
            initialValues={{ reason: selectedReason }}
          >
            <Collapse
              defaultActiveKey={2}
              ghost
              destroyInactivePanel
              items={Object.keys(groupedReasons).map((title, index) => ({
                key: index,
                label: <Link.$ className={styles.reschedule__reasonTitle}>{title} </Link.$>,
                children: (
                  <Radio.$
                    name="reason"
                    columns={1}
                    options={groupedReasons[title as keyof typeof groupedReasons]?.map((reason) => {
                      return { label: reason.description, value: reason.ID.toString() };
                    })}
                  />
                ),
              }))}
            />
          </Form>
        )}
      </Section.$>
    );
  });
}
